import axios from 'axios';

const BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE}/api`;

const api = axios.create({ baseURL: API, headers: { 'Content-Type': 'application/json' } });

export const apiBase = API;
export const appBase = BASE;

export const Shows = {
  list: () => api.get('/shows').then(r => r.data)
};

export const Guests = {
  list: (showId) => api.get('/guests', { params: showId ? { showId } : {} }).then(r => r.data),
  get: (id) => api.get(`/guests/${id}`).then(r => r.data),
  create: (payload) => api.post('/guests', payload).then(r => r.data),
  update: (id, patch) => api.patch(`/guests/${id}`, patch).then(r => r.data),
  remove: (id) => api.delete(`/guests/${id}`).then(r => r.data),
  sendEmail: (id, payload) => api.post(`/guests/${id}/send-email`, payload).then(r => r.data),
  aiDraft: (id, intent) => api.post(`/guests/${id}/ai-draft`, { intent }).then(r => r.data),
  bulkEmail: (payload) => api.post('/guests/bulk-email', payload).then(r => r.data),
  promoteToProspect: (id, payload) => api.post(`/guests/${id}/promote-to-prospect`, payload || {}).then(r => r.data),
  talkingPointsDefaultPrompt: () => api.get('/guests/talking-points/default-prompt').then(r => r.data),
  aiTalkingPoints: (id, payload) => api.post(`/guests/${id}/ai-talking-points`, payload || {}, { timeout: 240000 }).then(r => r.data),
  findPlatforms: (id, overwrite = false) => api.post(`/guests/${id}/find-platforms`, null, { params: { overwrite } }).then(r => r.data),
  bulkFindPlatforms: (showId, onlyMissing = true) => api.post('/guests/bulk-find-platforms', null, { params: { showId, onlyMissing }, timeout: 300000 }).then(r => r.data),
  listEmails: (id) => api.get(`/guests/${id}/emails`).then(r => r.data),
  syncCalendar: (id) => api.post(`/guests/${id}/calendar-event`).then(r => r.data)
};

export const Templates = {
  list: () => api.get('/templates').then(r => r.data),
  create: (t) => api.post('/templates', t).then(r => r.data),
  update: (id, t) => api.patch(`/templates/${id}`, t).then(r => r.data),
  remove: (id) => api.delete(`/templates/${id}`).then(r => r.data)
};

export const Prospects = {
  list: (showId, status) => api.get('/prospects', { params: { showId, status } }).then(r => r.data),
  get: (id) => api.get(`/prospects/${id}`).then(r => r.data),
  create: (p) => api.post('/prospects', p).then(r => r.data),
  update: (id, patch) => api.patch(`/prospects/${id}`, patch).then(r => r.data),
  remove: (id) => api.delete(`/prospects/${id}`).then(r => r.data),
  sendLetter: (id, payload) => api.post(`/prospects/${id}/send-letter`, payload).then(r => r.data),
  aiLetter: (id, intent) => api.post(`/prospects/${id}/ai-letter`, { intent }).then(r => r.data),
  listEmails: (id) => api.get(`/prospects/${id}/emails`).then(r => r.data)
};

export const Sweep = {
  start: (dateFrom, dateTo) => api.post('/inbox-sweep', { dateFrom, dateTo }).then(r => r.data),
  status: () => api.get('/inbox-sweep/status').then(r => r.data),
  candidates: (status = 'new') => api.get('/inbox-sweep/candidates', { params: { status } }).then(r => r.data),
  dismiss: (id) => api.post(`/inbox-sweep/candidates/${id}/dismiss`).then(r => r.data),
  add: (id, addAs, showId) => api.post(`/inbox-sweep/candidates/${id}/add`, { addAs, showId }).then(r => r.data)
};

export const Release = {
  get: (guestId) => api.get(`/release/${guestId}`).then(r => r.data),
  submit: (guestId, payload) => api.post(`/release/${guestId}`, payload).then(r => r.data),
  listSubmissions: (showId, guestId) => api.get('/release-submissions', { params: { showId, guestId } }).then(r => r.data),
  getSubmission: (id) => api.get(`/release-submissions/${id}`).then(r => r.data),
  pdfUrl: (id) => `${API}/release-submissions/${id}/pdf`
};

export const Booking = {
  options: (showId, prospect) => api.get(`/booking/${showId}`, { params: prospect ? { prospect } : {} }).then(r => r.data),
  create: (showId, payload, prospect) => api.post(`/booking/${showId}`, payload, { params: prospect ? { prospect } : {} }).then(r => r.data)
};

export const Files = {
  upload: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API}/files`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  }
};

export const Analytics = {
  get: (showId) => api.get('/analytics', { params: showId ? { showId } : {} }).then(r => r.data)
};

export const Automations = {
  autoPublishNow: () => api.post('/automations/auto-publish-now').then(r => r.data),
  autoPublishStatus: () => api.get('/automations/auto-publish-status').then(r => r.data),
  status: () => api.get('/automations/status').then(r => r.data),
  runNow: () => api.post('/automations/run-now').then(r => r.data),
  searchMentions: () => api.post('/automations/search-mentions').then(r => r.data),
  recentEmails: (showId, limit = 8) => api.get('/email-log/recent', { params: { showId, limit } }).then(r => r.data)
};

export const Mentions = {
  list: (showId, guestId) => api.get('/mentions', { params: { showId, guestId } }).then(r => r.data),
  remove: (id) => api.delete(`/mentions/${id}`).then(r => r.data),
  searchNow: () => api.post('/automations/search-mentions').then(r => r.data)
};

export const Settings = {
  get: () => api.get('/settings').then(r => r.data),
  update: (patch) => api.patch('/settings', patch).then(r => r.data),
  msStatus: () => api.get('/auth/microsoft/status').then(r => r.data),
  msDisconnect: () => api.post('/auth/microsoft/disconnect').then(r => r.data),
  msLoginUrl: () => `${API}/auth/microsoft/login`,
  resetData: () => api.post('/admin/reset-data', { confirm: 'RESET' }).then(r => r.data),
  exportDataUrl: () => `${API}/admin/export-data`,
  emailBackupNow: () => api.post('/admin/email-backup-now').then(r => r.data),
  airtablePreview: (payload) => api.post('/admin/airtable/preview', payload, { timeout: 180000 }).then(r => r.data),
  airtableRun: (payload) => api.post('/admin/airtable/run', payload).then(r => r.data),
  airtableStatus: () => api.get('/admin/airtable/status').then(r => r.data),
  airtableImportedCount: () => api.get('/admin/airtable/imported-count').then(r => r.data),
  airtableUndo: () => api.post('/admin/airtable/undo').then(r => r.data),
  restoreData: (file, overwrite = false) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/admin/restore-data?overwrite=${overwrite}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(r => r.data);
  }
};

export default api;
